import {
  getDataObjectRoute,
  getProcessDetailRoute,
  getTaskRoute
} from "@certa/common/utils/routes";
import type { ModelledAPIResponse } from "@certa/queries/types/globalSearch.types";
import { useNavigate } from "react-router";
import type { SearchItem } from "../GlobalSearchWithSDO.schema";
import {
  transformHighlights,
  transformSearchResult
} from "../utils/searchTransformers";
import { useCallback } from "react";
import type { ListInteractionMode } from "@certa/catalyst/hooks/useListInteraction";
import { analyticsService, AnalyticsEvents } from "@certa/analytics";
import { useGetPreviewData } from "./useSearchKeyboard";
import { useSearchStore } from "../store/useSearchStore";
import { useGlobalSearch } from "./useGlobalSearch";
import type { SearchResultType } from "../GlobalSearchWithSDO.constants";

type UseSearchNavigationProps = {
  setRecentSearches: (item: SearchItem) => void;
  onClose: () => void;
  keyboardCount: number;
};

const getActiveIndex = (
  selectedItem: number,
  selectedResultType: SearchResultType | null,
  type: ModelledAPIResponse.SearchResult["type"],
  id: string,
  previewData: ReturnType<typeof useGetPreviewData>
) => {
  let adjustedIndex = selectedItem;

  if (!selectedResultType) {
    if (type === "workflow")
      adjustedIndex = previewData.workflows?.findIndex(
        item => item.id.toString() === id
      );
    if (type === "object")
      adjustedIndex = previewData.data_records?.findIndex(
        item => item.id.toString() === id
      );
    if (type === "file")
      adjustedIndex = previewData.files?.findIndex(
        item => item.id.toString() === id
      );
  }
  return adjustedIndex + 1;
};

export const useSearchNavigation = ({
  setRecentSearches,
  onClose,
  keyboardCount
}: UseSearchNavigationProps) => {
  const navigate = useNavigate();

  const { selectedResultType } = useSearchStore();
  const allSearchResults = useGlobalSearch();
  const previewData = useGetPreviewData(allSearchResults);

  const onSelectSearchResult = useCallback(
    (
      result: ModelledAPIResponse.SearchResult,
      index: number,
      interactionMode: ListInteractionMode,
      query: string
    ) => {
      if (!result) return;
      const transformedResult = transformSearchResult(result);

      const { text, shouldShow } = transformHighlights(result);

      const subDescription = shouldShow ? text : undefined;

      if (result.type === "workflow") {
        navigate(getProcessDetailRoute(result.id));
      }
      if (result.type === "object") {
        navigate(
          getDataObjectRoute({
            objectType: result.objectType.tag,
            objectId: result.objectInstance.uid,
            objectTab: "overview"
          })
        );
      } else if (result.type === "file" || result.type === "field") {
        navigate(
          getTaskRoute({
            processId: result.workflow.id,
            taskId: result.step.id
          })
        );
      }
      if (transformedResult) {
        setRecentSearches({
          ...transformedResult,
          subDescription
        });
      }

      const adjustedIndex = getActiveIndex(
        index,
        selectedResultType,
        result.type,
        result.id.toString(),
        previewData
      );

      let name = "";
      if ("name" in result) {
        name = result.name;
      } else if ("fileName" in result) {
        name = result.fileName;
      }

      analyticsService.track(
        AnalyticsEvents.globalSearchEvents.GLOBAL_SEARCH_RESULT_CLICK,
        {
          result_type: result.type,
          interaction_mode: interactionMode,
          result_index: adjustedIndex,
          name,
          is_preview_mode: selectedResultType ? false : true,
          keyword_search: query,
          certa_version: "OS3",
          keyboard_count: keyboardCount
        }
      );
      onClose();
    },
    [
      selectedResultType,
      previewData,
      onClose,
      navigate,
      setRecentSearches,
      keyboardCount
    ]
  );

  const onSelectRecentSearch = useCallback(
    (item: SearchItem, index: number, interactionMode: ListInteractionMode) => {
      if (item.type === "workflow") {
        navigate(getProcessDetailRoute(item.id));
      }
      if (item.type === "object") {
        navigate(
          getDataObjectRoute({
            objectType: item.objectType,
            objectId: item.objectId,
            objectTab: "overview"
          })
        );
      } else if (item.type === "file" || item.type === "field") {
        navigate(
          getTaskRoute({
            processId: item.workflowId,
            taskId: item.stepId
          })
        );
      }
      setRecentSearches(item);
      analyticsService.track(
        AnalyticsEvents.globalSearchEvents.GLOBAL_SEARCH_RECENT_SEARCH_CLICK,
        {
          result_type: item.type,
          interaction_mode: interactionMode,
          result_index: index + 1,
          certa_version: "OS3"
        }
      );
      onClose();
    },
    [navigate, setRecentSearches, onClose]
  );

  return { onSelectSearchResult, onSelectRecentSearch };
};
